import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { apiCall, API_ENDPOINTS } from '../config/api';
import { weatherCache } from '../utils/weatherCache';
import '../styles/TravelPlansModal.css';

/**
 * TravelPlansModal allows users to manage their travel locations.
 * Locations are used to display weather for the correct city on the calendar.
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Callback for when user closes the modal
 * @param {Function} onLocationAdded - Callback after a location is successfully added
 * @param {Array} locations - Existing location entries
 */
function TravelPlansModal({ isOpen, onClose, onLocationAdded, locations }) {
  const [formData, setFormData] = useState({
    cityName: '',
    coordinates: '',
    startDate: '',
    endDate: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.cityName || !formData.coordinates || !formData.startDate || !formData.endDate) {
      setError('All fields are required');
      setLoading(false);
      return;
    }

    // Parse "lat, long" format from Google Maps
    const parts = formData.coordinates.split(',').map(s => s.trim());
    if (parts.length !== 2 || parts.some(p => isNaN(parseFloat(p)))) {
      setError('Invalid coordinates format. Use "latitude, longitude" (e.g., 32.9886, -96.9007)');
      setLoading(false);
      return;
    }
    const latitude = parseFloat(parts[0]);
    const longitude = parseFloat(parts[1]);

    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      setError('Start date must be before or equal to end date');
      setLoading(false);
      return;
    }

    try {
      const response = await apiCall(API_ENDPOINTS.LOCATIONS, {
        method: 'POST',
        body: JSON.stringify({
          cityName: formData.cityName,
          latitude,
          longitude,
          startDate: formData.startDate,
          endDate: formData.endDate
        })
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to add location');
        setLoading(false);
        return;
      }

      const newLocation = await response.json();

      // Clear weather cache so next fetch uses the new location
      weatherCache.clear();

      setFormData({ cityName: '', coordinates: '', startDate: '', endDate: '' });
      onLocationAdded(newLocation);
    } catch (err) {
      setError('Error adding location: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="travel-plans-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Travel Plans</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="travel-plans-modal-body">
          {locations.length > 0 && (
            <div className="locations-list">
              <h3>Your Travel Locations</h3>
              <ul>
                {locations.map(loc => (
                  <li key={loc.id} className="location-item">
                    <strong>{loc.cityName}</strong>
                    <span className="location-dates">{loc.startDate} to {loc.endDate}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="location-form">
            <h3>Add New Location</h3>

            <div className="form-group">
              <label htmlFor="cityName">City Name</label>
              <input
                id="cityName"
                type="text"
                name="cityName"
                value={formData.cityName}
                onChange={handleChange}
                placeholder="e.g., Denver, CO"
              />
            </div>

            <div className="form-group">
              <label htmlFor="coordinates">Coordinates</label>
              <input
                id="coordinates"
                type="text"
                name="coordinates"
                value={formData.coordinates}
                onChange={handleChange}
                placeholder="e.g., 39.7392, -104.9903"
              />
              <small>Copy lat/long from Google Maps and paste here</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  id="startDate"
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  id="endDate"
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                />
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? 'Adding...' : 'Add Location'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

TravelPlansModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onLocationAdded: PropTypes.func.isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    cityName: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }))
};

TravelPlansModal.defaultProps = {
  locations: []
};

export default TravelPlansModal;
